import { useState } from "react";
import { reportPostAction } from "../../redux/actions/postActions";
import { useDispatch } from "react-redux";
import { useToast } from "../../contexts/ToastContext";

import { Dialog } from "@headlessui/react";
import ButtonLoadingSpinner from "../loader/ButtonLoadingSpinner";

const ReportPostModal = ({
  isOpen,
  onClose,
  postId,
  setReportedPost,
}) => {
  const [selectedReason, setSelectedReason] = useState("");
  const [customReportReason, setCustomReportReason] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const { success: showSuccess, error: showError } = useToast();

  const reportReasons = [
    "Spam or misleading content",
    "Harassment or bullying",
    "Hate speech or offensive content",
    "Violence or dangerous content",
    "Copyright infringement",
    "Inappropriate or explicit content",
    "False information",
    "Other"
  ];

  const handleReportSubmit = async () => {
    // Determine the final reason
    const finalReason = selectedReason === "Other" 
      ? customReportReason.trim() 
      : selectedReason;

    if (!selectedReason) {
      setError("Please select a reason for reporting this post.");
      return;
    }

    if (selectedReason === "Other" && !customReportReason.trim()) {
      setError("Please provide a reason for reporting this post.");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const result = await dispatch(reportPostAction(postId, finalReason));
      setIsLoading(false);
      
      if (result && result.success) {
        showSuccess("Post reported successfully. Thank you for your feedback.");
        if (setReportedPost) {
          setReportedPost(true);
        }
        setSelectedReason("");
        setCustomReportReason("");
        onClose();
      } else {
        const errorMessage = result?.error || "Failed to report post. Please try again.";
        setError(errorMessage);
        showError(errorMessage);
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage = error.message || "An error occurred. Please try again.";
      setError(errorMessage);
      showError(errorMessage);
    }
  };

  const handleClose = () => {
    setSelectedReason("");
    setCustomReportReason("");
    setError("");
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      className="fixed inset-0 z-50 overflow-y-auto"
    >
      <div className="fixed inset-0 flex items-center justify-center">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />

        <div className="inline-block align-middle bg-white rounded-md text-left shadow-xl transform transition-all w-full mx-4 md:max-w-2xl">
          <Dialog.Title
            as="h3"
            className="text-lg font-roboto-flex leading-6 text-gray-900 p-4"
          >
            Report Post
          </Dialog.Title>

          <div className="p-4">
            <label className="block text-sm font-roboto-flex text-gray-700 mb-3">
              Reason for report
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {reportReasons.map((reason) => (
                <label
                  key={reason}
                  className="flex items-center cursor-pointer hover:bg-gray-50 p-2 rounded-md transition-colors"
                >
                  <input
                    type="radio"
                    name="report-reason"
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => {
                      setSelectedReason(e.target.value);
                      if (e.target.value !== "Other") {
                        setCustomReportReason("");
                      }
                      setError("");
                    }}
                    className="mr-3 h-4 w-4 text-primary-700 focus:ring-primary-500 border-gray-300"
                  />
                  <span className="text-sm font-roboto-flex text-gray-700">
                    {reason}
                  </span>
                </label>
              ))}
            </div>

            {selectedReason === "Other" && (
              <div className="mt-4">
                <label
                  htmlFor="custom-report-reason"
                  className="block text-sm font-roboto-flex text-gray-700 mb-2"
                >
                  Please specify the reason
                </label>
                <textarea
                  name="custom-report-reason"
                  id="custom-report-reason"
                  className="shadow-sm block w-full sm:text-sm rounded-md h-24 resize-none p-3 focus:outline-none focus:border border-gray-300"
                  value={customReportReason}
                  onChange={(e) => {
                    setCustomReportReason(e.target.value);
                    setError("");
                  }}
                  placeholder="Please provide a reason for reporting this post..."
                />
              </div>
            )}

            {error && (
              <p className="mt-2 text-sm text-red-600 font-roboto-flex">{error}</p>
            )}
          </div>

          <div className="flex justify-end p-4">
            <button
              disabled={isLoading || !selectedReason || (selectedReason === "Other" && !customReportReason.trim())}
              type="button"
              className={`inline-flex justify-center px-4 py-2 text-sm font-medium text-white border rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 font-roboto-flex ${
                isLoading || !selectedReason || (selectedReason === "Other" && !customReportReason.trim())
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-primary-700 border-transparent hover:bg-primary-800 focus-visible:ring-primary-500"
              }`}
              onClick={handleReportSubmit}
            >
              {isLoading ? (
                <ButtonLoadingSpinner loadingText={"Reporting..."} />
              ) : (
                "Report"
              )}
            </button>

            <button
              type="button"
              className="inline-flex justify-center px-4 py-2 ml-4 text-sm font-medium text-gray-700 bg-gray-100 border border-transparent rounded-md hover:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500 font-roboto-flex"
              onClick={handleClose}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default ReportPostModal;
